import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useTradingAccounts, TradingAccount } from '@/hooks/useTradingAccounts';
import { supabase } from '@/integrations/supabase/client';

interface AccountContextType {
  accounts: TradingAccount[];
  activeAccount: TradingAccount | null;
  loading: boolean;
  setActive: (accountId: string | null) => Promise<void>;
  refreshAccounts: () => Promise<void>;
  createAccount: (account: Omit<TradingAccount, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<any>;
  updateAccount: (accountId: string, updates: Partial<TradingAccount>) => Promise<void>;
  deleteAccount: (accountId: string) => Promise<void>;
  toggleFavorite: (accountId: string) => Promise<void>;
  groupAccountsByFirm: () => { [firmName: string]: TradingAccount[] };
  groupAccountsByType: () => {
    challenges: { [firmName: string]: TradingAccount[] };
    funded: { [firmName: string]: TradingAccount[] };
    personal: { [firmName: string]: TradingAccount[] };
  };
  favoriteAccounts: TradingAccount[];
  recentAccounts: TradingAccount[];
  detectAndUpdateFirms: () => Promise<{ updated: number; results: any[] }>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  const accountData = useTradingAccounts(userId);

  return (
    <AccountContext.Provider value={accountData}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccountContext() {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccountContext must be used within an AccountProvider');
  }
  return context;
}
